// ChatController.js
// Ce fichier gère la logique de chat pour l'application.

import MicAnimation from '../views/MicAnimation';
import { fetchClients, createInvoice, fetchInvoices, fetchQuotes, createQuote } from '../models/InvoiceService';
import { getUserInfo } from '../models/UserInfoService';
import { getUserAbsences } from '../models/userAbsencesService';
import { openPortail } from '../models/shellyService';

export const init = async (setLoading, peerConnectionRef, dataChannelRef, setResponseTexts) => {
    setLoading(true);

    try {
        const tokenResponse = await fetch(
            "https://us-central1-theapp-428906.cloudfunctions.net/getEphemeralKey"
        );
        const data = await tokenResponse.json();
        const EPHEMERAL_KEY = data.client_secret.value;

        const pc = new RTCPeerConnection();
        peerConnectionRef.current = pc;

        const audioEl = document.createElement("audio");
        audioEl.autoplay = true;
        pc.ontrack = (e) => (audioEl.srcObject = e.streams[0]);

        const mediaStream = await navigator.mediaDevices.getUserMedia({
            audio: true,
        });
        pc.addTrack(mediaStream.getTracks()[0]);

        const dc = pc.createDataChannel("oai-events");
        dataChannelRef.current = dc;
        console.log('DataChannelRef current:', dataChannelRef.current);

        dc.addEventListener("open", () => {
            console.log("DataChannel ouvert, on envoie le session.update !");
            dc.send(JSON.stringify({
                type: "session.update",
                event_id: "update_transcription_auto",
                session: {
                    input_audio_transcription: { model: "whisper-1" }
                }
            }));
        });

        dc.addEventListener("message", async (e) => {
            const message = JSON.parse(e.data);
            handleEvent(message, setResponseTexts, dataChannelRef);
        });

        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);

        const baseUrl = "https://api.openai.com/v1/realtime";
        const model = "gpt-4o-realtime-preview";
        const sdpResponse = await fetch(`${baseUrl}?model=${model}`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${EPHEMERAL_KEY}`,
                "Content-Type": "application/sdp",
            },
            body: offer.sdp,
        });

        const answer = { type: "answer", sdp: await sdpResponse.text() };
        await pc.setRemoteDescription(answer);

        setLoading(false);
    } catch (error) {
        console.error("Erreur d'initialisation WebRTC :", error);
    }
};

export const startConversation = (chatActive, setChatActive, peerConnectionRef, init, setLoading, dataChannelRef, setResponseTexts) => {
    if (chatActive) {
        if (peerConnectionRef.current) {
            peerConnectionRef.current.close();
            peerConnectionRef.current = null;
        }
        setChatActive(false);
    } else {
        init(setLoading, peerConnectionRef, dataChannelRef, setResponseTexts);
        setChatActive(true);
    }
};

export const handleEvent = async (message, setResponseTexts, dataChannelRef) => {
    const { type, item_id, transcript } = message;
    // console.log("handleEvent", message);
    if (type === 'input_audio_buffer.speech_started') {
        setResponseTexts((prevTexts) => [
            ...prevTexts,
            { item_id, message: <MicAnimation />, isTranscribing: true, type: "user" },
        ]);
    } else if (type === 'conversation.item.input_audio_transcription.completed') {
        setResponseTexts((prevTexts) => {
            const messageIndex = prevTexts.findIndex((msg) => msg.isTranscribing);
            if (messageIndex !== -1) {
                const updatedMessages = [...prevTexts];
                updatedMessages[messageIndex] = { ...updatedMessages[messageIndex], message: transcript, isTranscribing: false };
                return updatedMessages;
            } else {
                return [...prevTexts, { message: transcript, isTranscribing: false, type: "user" }];
            }
        });
    } else if (message.type === "text") {
        setResponseTexts((prevTexts) => [...prevTexts, { message: message.response.text, type: "chatgpt" }]);
    } else if (message?.type === "response.audio_transcript.delta") {
        const { response_id, delta } = message;
        setResponseTexts((prevTexts) => {
            const existingMessageIndex = prevTexts.findIndex(text => text.response_id === response_id);
            if (existingMessageIndex !== -1) {
                const updatedMessage = {
                    ...prevTexts[existingMessageIndex],
                    message: prevTexts[existingMessageIndex].message + delta
                };
                return [
                    ...prevTexts.slice(0, existingMessageIndex),
                    updatedMessage,
                    ...prevTexts.slice(existingMessageIndex + 1)
                ];
            } else {
                return [...prevTexts, { response_id, message: delta }];
            }
        });
    } else if (message.type === "response.function_call_arguments.done") {
        if (message.name === "create_invoice") {
            const args = JSON.parse(message.arguments);
            console.log("function call arguments done", message.name, args);
            try {
                const invoiceId = await createInvoice(args);
                console.log("Invoice created with ID:", invoiceId);
                const userMessage = {
                    type: "response.create",
                    response: {
                        modalities: ["text", "audio"],
                        instructions: `Une facture a été créée pour le client ${args.client} avec le num��ro ${args.num} pour le montant de ${args.total} euros. ID de la facture: ${invoiceId}.`,
                    }
                };
                dataChannelRef.current.send(JSON.stringify(userMessage));
            } catch (error) {
                console.error("Erreur lors de la création de la facture:", error);
            }
        }
        if (message.name === "create_quote") {
            const args = JSON.parse(message.arguments);
            console.log("function call arguments done", message.name, args);
            try {
                const quoteId = await createQuote(args);
                console.log("Quote created with ID:", quoteId);
                const userMessage = {
                    type: "response.create",
                    response: {
                        modalities: ["text", "audio"],
                        instructions: `Un devis a été créé pour le client ${args.client} avec le numéro ${args.num} pour le montant de ${args.total} euros. ID du devis: ${quoteId}.`,
                    }
                };
                dataChannelRef.current.send(JSON.stringify(userMessage));
            } catch (error) {
                console.error("Erreur lors de la création du devis:", error);
            }
        }
        if (message.name === "get_clients_list") {
            const clients = (await fetchClients()).map(client => ({
                ...client,
                client: client.Title
            }));
            console.log("clients", clients);
            const event = {
                event_id: "event_clients_list",
                type: "conversation.item.create",
                previous_item_id: null,
                item: {
                    id: "msg_clients_list",
                    type: "message",
                    role: "user",
                    content: [
                        {
                            type: "input_text",
                            text: JSON.stringify(clients)
                        }
                    ]
                }
            };
            console.log(dataChannelRef);
            dataChannelRef.current.send(JSON.stringify(event));
            const userMessage = {
                type: "response.create",
                response: {
                    modalities: ["text", "audio"],
                    instructions: "Liste des clients uniquement le nom du client.",
                }
            };
            dataChannelRef.current.send(JSON.stringify(userMessage));
        }
        if (message.name === "get_invoices_list") {
            const invoices = (await fetchInvoices()).map(invoice => ({
                ...invoice,
                invoiceNumber: invoice.num
            }));
            console.log("invoices", invoices);
            const event = {
                event_id: "event_invoices_list",
                type: "conversation.item.create",
                previous_item_id: null,
                item: {
                    id: "msg_invoices_list",
                    type: "message",
                    role: "user",
                    content: [
                        {
                            type: "input_text",
                            text: JSON.stringify(invoices)
                        }
                    ]
                }
            };
            dataChannelRef.current.send(JSON.stringify(event));
            const userMessage = {
                type: "response.create",
                response: {
                    modalities: ["text", "audio"],
                    instructions: "Liste des factures avec le numéro de facture.",
                }
            };
            dataChannelRef.current.send(JSON.stringify(userMessage));
        }
        if (message.name === "get_quotes_list") {
            const quotes = (await fetchQuotes()).map(quote => ({
                ...quote,
                quoteNumber: quote.num
            }));
            console.log("quotes", quotes);
            const event = {
                event_id: "event_quotes_list",
                type: "conversation.item.create",
                previous_item_id: null,
                item: {
                    id: "msg_quotes_list",
                    type: "message",
                    role: "user",
                    content: [
                        {
                            type: "input_text",
                            text: JSON.stringify(quotes)
                        }
                    ]
                }
            };
            dataChannelRef.current.send(JSON.stringify(event));
            const userMessage = {
                type: "response.create",
                response: {
                    modalities: ["text", "audio"],
                    instructions: "Liste des devis avec le numéro de devis.",
                }
            };
            dataChannelRef.current.send(JSON.stringify(userMessage));
        }
        if (message.name === "get_userInfo") {
            const args = JSON.parse(message.arguments)
            const userInfo = await getUserInfo(args.upn);
            console.log("userInfo", userInfo);
            const event = {
                event_id: "event_user_info",
                type: "conversation.item.create",
                previous_item_id: null,
                item: {
                    id: "msg_user_info",
                    type: "message",
                    role: "user",
                    content: [{ type: "input_text", text: JSON.stringify(userInfo) }]
                }
            };
            dataChannelRef.current.send(JSON.stringify(event));
            const userMessage = {
                type: "response.create",
                response: {
                    modalities: ["text", "audio"],
                    instructions: "Liste les informations de l'utilisateur.",
                }
            };
            dataChannelRef.current.send(JSON.stringify(userMessage));
        }
        if (message.name === "get_userAbsences") {
            const args = JSON.parse(message.arguments)
            const userAbsences = await getUserAbsences(args.upn);
            console.log("userAbsences", JSON.stringify(userAbsences), args);
            const event = {
                event_id: "event_user_absences",
                type: "conversation.item.create",
                previous_item_id: null,
                item: {
                    id: "msg_user_absences",
                    type: "message",
                    role: "user",
                    content: [{ type: "input_text", text: JSON.stringify(userAbsences) }]
                }
            };
            dataChannelRef.current.send(JSON.stringify(event));
            const userMessage = {
                type: "response.create",
                response: {
                    modalities: ["text", "audio"],
                    instructions: "Liste les absences de l'utilisateur.",
                }
            };
            dataChannelRef.current.send(JSON.stringify(userMessage));
        }
        if (message.name === "open_portail") {
            console.log("open_portail", message);
            openPortail();
            const userMessage = {
                type: "response.create",
                response: {
                    modalities: ["text", "audio"],
                    instructions: "Le portail a été ouvert.",
                }
            };
            dataChannelRef.current.send(JSON.stringify(userMessage));
        }
    }
    if (message.type === "user_message") {
        console.log("user_message", message);
        const userMessage = {
            type: "conversation.item.create",
            item: {
                type: "message",
                role: "user",
                content: [{
                    type: "input_text",
                    text: message.content
                }]
            }
        };
        dataChannelRef.current.send(JSON.stringify(userMessage));

        // Demander une réponse de ChatGPT
        const responseRequest = {
            type: "response.create",
            response: {
                modalities: ["text", "audio"],
                instructions: "Répondez au message de l'utilisateur."
            }
        };
        dataChannelRef.current.send(JSON.stringify(responseRequest));
    }
}; 